import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiClock, FiMapPin, FiPlus, FiMinus, FiSearch, FiStar, FiX } from 'react-icons/fi';
import { IoRestaurantOutline, IoBicycleOutline, IoLogoWhatsapp } from 'react-icons/io5';
import { LoaderComponent, EmptyState } from '../../components/ui';
import { api } from '../../services/api';
import { useFoodCart } from '../../contexts/FoodCartContext';

interface Restaurant {
  id: number;
  name: string;
  description?: string;
  category?: string;
  imageUrl?: string;
  address?: string;
  openTime?: string;
  closeTime?: string;
  deliveryTimeMin?: number;
  deliveryFee?: number;
  deliveryFeeDesde?: boolean; // modo «por distância»: o valor é o mínimo
  whatsapp?: string | null; // só dígitos com indicativo
  ratingMedia?: number | string | null; // média da comida; só se mostra a partir de 5 avaliações
  ratingCount?: number;
  status: string;
}

interface MenuItem {
  id: number;
  name: string;
  description?: string;
  price: number;
  imageUrl?: string;
  prepTime?: number;
  available: boolean;
  categoryId?: number;
  categoryName?: string;
}

interface MenuCategory {
  id: number;
  name: string;
  order: number;
}

interface CartItem extends MenuItem {
  quantity: number;
}

/** Imagem com recurso: se o endereço não abrir (ex.: imagens antigas da Faturali), mostra o ícone. */
const ImagemPrato: React.FC<{ src?: string; alt: string; className?: string; icone?: string }> = ({ src, alt, className, icone = 'text-4xl' }) => {
  const [falhou, setFalhou] = useState(false);
  if (!src || falhou) {
    return (
      <div className="flex h-full w-full items-center justify-center text-pagali-gray-300">
        <IoRestaurantOutline className={icone} />
      </div>
    );
  }
  return <img src={src} alt={alt} loading="lazy" className={className} onError={() => setFalhou(true)} />;
};

const Restaurant: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [pesquisa, setPesquisa] = useState('');
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const foodCart = useFoodCart();

  useEffect(() => {
    if (id) {
      fetchRestaurantData();
    }
  }, [id]);

  const fetchRestaurantData = async () => {
    try {
      setLoading(true);
      const [restaurantRes, menuRes] = await Promise.all([
        api.get(`/food/restaurants/${id}`),
        api.get(`/food/restaurants/${id}/menu`),
      ]);

      setRestaurant(restaurantRes.data.data);
      setMenuItems(menuRes.data.data.items || []);
      setCategories(menuRes.data.data.categories || []);
    } catch (error) {
      console.error('Error fetching restaurant:', error);
    } finally {
      setLoading(false);
    }
  };

  const cart = foodCart.cart;

  const addToCart = (item: MenuItem) => {
    if (restaurant) {
      foodCart.addItem(
        { id: item.id, name: item.name, price: Number(item.price), imageUrl: item.imageUrl, categoryName: item.categoryName },
        { id: restaurant.id, name: restaurant.name, deliveryFee: Number(restaurant.deliveryFee || 0), deliveryTimeMin: restaurant.deliveryTimeMin }
      );
    }
  };

  const removeFromCart = (itemId: number) => {
    foodCart.removeItem(itemId);
  };

  const getItemQuantity = (itemId: number) => {
    return cart.find((i) => i.id === itemId)?.quantity || 0;
  };

  const cartTotal = foodCart.cartTotal;
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const filteredItems = selectedCategory
    ? menuItems.filter((item) => item.categoryId === selectedCategory)
    : menuItems;

  const formatPrice = (price: number | string) => {
    return `${Number(price || 0).toFixed(0)}$`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoaderComponent />
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <EmptyState
          icon={<IoRestaurantOutline className="text-4xl" />}
          title="Restaurante não encontrado"
          description="O restaurante que procura não existe ou foi removido"
        />
      </div>
    );
  }

  const capa = restaurant.imageUrl || menuItems.find((i) => i.imageUrl)?.imageUrl;
  const aberto = (() => {
    if (!restaurant.openTime || !restaurant.closeTime) return true;
    const min = (h: string) => { const [a, b] = h.split(':').map(Number); return a * 60 + (b || 0); };
    const agora = new Date().getHours() * 60 + new Date().getMinutes();
    const a = min(restaurant.openTime), f = min(restaurant.closeTime);
    return a <= f ? agora >= a && agora < f : agora >= a || agora < f;
  })();
  const q = pesquisa.trim().toLowerCase();
  const pratos = filteredItems.filter((i) => !q || i.name.toLowerCase().includes(q) || (i.description || '').toLowerCase().includes(q));
  const contaPorCategoria = (cid: number) => menuItems.filter((i) => i.categoryId === cid).length;
  const categoriasComPratos = categories.filter((c) => contaPorCategoria(c.id) > 0);

  const BotaoCategoria = ({ id: cid, nome, n }: { id: number | null; nome: string; n: number }) => (
    <button
      onClick={() => setSelectedCategory(cid)}
      className={`flex w-full items-center justify-between gap-2 whitespace-nowrap rounded-xl px-3 py-2 text-left text-sm font-medium transition-colors lg:whitespace-normal ${
        selectedCategory === cid
          ? 'bg-pagali-purple text-white'
          : 'bg-white text-pagali-gray-600 hover:bg-pagali-gray-100 lg:bg-transparent'
      }`}
    >
      <span>{nome}</span>
      <span className={`text-xs ${selectedCategory === cid ? 'text-white/80' : 'text-pagali-gray-400'}`}>{n}</span>
    </button>
  );

  return (
    <div className="min-h-screen pb-32">
      {/* Capa */}
      <div className="relative h-48 overflow-hidden rounded-3xl bg-gradient-to-br from-pagali-purple to-pagali-purple-light md:h-64">
        {capa && <img src={capa} alt={restaurant.name} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <button
          onClick={() => navigate('/food')}
          aria-label="Voltar aos restaurantes"
          className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-pagali-gray-800 shadow-card hover:bg-white"
        >
          <FiArrowLeft />
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8">
          <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold text-white ${aberto ? 'bg-pagali-success' : 'bg-pagali-gray-800/80'}`}>
            {aberto ? 'Aberto' : 'Fechado'}
          </span>
          <h1 className="mt-2 text-2xl font-bold text-white md:text-4xl">{restaurant.name}</h1>
          {restaurant.category && <p className="text-sm text-white/80">{restaurant.category}</p>}
        </div>
      </div>

      {/* Informação */}
      <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
        {restaurant.ratingMedia != null && Number(restaurant.ratingCount) >= 5 && (
          <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 font-semibold text-pagali-gray-800 shadow-card">
            <FiStar className="text-pagali-warning" /> {Number(restaurant.ratingMedia).toFixed(1)}
            <span className="font-normal text-pagali-gray-500">({restaurant.ratingCount} avaliações)</span>
          </span>
        )}
        {restaurant.openTime && restaurant.closeTime && (
          <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-pagali-gray-600 shadow-card">
            <FiClock /> {restaurant.openTime.slice(0, 5)} – {restaurant.closeTime.slice(0, 5)}
          </span>
        )}
        <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-pagali-gray-600 shadow-card">
          <IoBicycleOutline /> {restaurant.deliveryTimeMin || 30}-{(restaurant.deliveryTimeMin || 30) + 15} min ·{' '}
          {Number(restaurant.deliveryFee) ? `${restaurant.deliveryFeeDesde ? 'desde ' : ''}${Number(restaurant.deliveryFee).toFixed(0)}$ entrega` : 'entrega grátis'}
        </span>
        {restaurant.address && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${restaurant.name}, ${restaurant.address}, Cabo Verde`)}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-pagali-gray-600 shadow-card hover:text-pagali-purple"
          >
            <FiMapPin /> {restaurant.address}
          </a>
        )}
      </div>
      {restaurant.description && <p className="mt-3 max-w-3xl text-sm text-pagali-gray-600">{restaurant.description}</p>}

      {/* Ementa */}
      <div className="mt-6 lg:grid lg:grid-cols-[240px_1fr] lg:gap-8">
        {/* Categorias: faixa no telemóvel, coluna fixa no computador */}
        {categoriasComPratos.length > 0 && (
          <aside className="sticky top-0 z-20 -mx-4 bg-pagali-gray-50 px-4 py-2 lg:top-4 lg:mx-0 lg:self-start lg:bg-transparent lg:p-0">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide lg:max-h-[calc(100vh-8rem)] lg:flex-col lg:gap-1 lg:overflow-y-auto">
              <BotaoCategoria id={null} nome="Todos" n={menuItems.length} />
              {categoriasComPratos.map((c) => (
                <BotaoCategoria key={c.id} id={c.id} nome={c.name} n={contaPorCategoria(c.id)} />
              ))}
            </div>
          </aside>
        )}

        <section className="mt-4 lg:mt-0">
          <div className="relative mb-4">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-pagali-gray-400" />
            <input
              type="search"
              value={pesquisa}
              onChange={(e) => setPesquisa(e.target.value)}
              placeholder={`Procurar em ${menuItems.length} pratos…`}
              aria-label="Procurar prato"
              className="w-full rounded-2xl border border-pagali-gray-200 bg-white py-3 pl-11 pr-4 focus:border-pagali-purple focus:outline-none focus:ring-2 focus:ring-pagali-purple/20"
            />
          </div>

          {pratos.length === 0 ? (
            <EmptyState
              icon={<IoRestaurantOutline className="text-4xl" />}
              title="Nenhum prato encontrado"
              description={q ? 'Experimente procurar por outro nome.' : 'Esta categoria não tem pratos disponíveis.'}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {pratos.map((item) => {
                const quantity = getItemQuantity(item.id);
                return (
                  <div
                    key={item.id}
                    className={`flex flex-col overflow-hidden rounded-2xl bg-white shadow-card transition-shadow hover:shadow-card-hover ${!item.available ? 'opacity-50' : ''}`}
                  >
                    <button onClick={() => setSelectedItem(item)} className="relative aspect-[4/3] bg-pagali-gray-100 text-left">
                      <ImagemPrato src={item.imageUrl} alt={item.name} className="h-full w-full object-cover" />
                    </button>
                    <div className="flex flex-1 flex-col p-4">
                      <button onClick={() => setSelectedItem(item)} className="text-left">
                        <h3 className="line-clamp-2 font-semibold text-pagali-gray-800">{item.name}</h3>
                        {item.description && <p className="mt-1 line-clamp-2 text-sm text-pagali-gray-500">{item.description}</p>}
                      </button>
                      <div className="mt-auto flex items-center justify-between pt-3">
                        <span className="text-lg font-bold text-pagali-purple">{formatPrice(item.price)}</span>
                        {item.available ? (
                          <div className="flex items-center gap-2">
                            {quantity > 0 && (
                              <>
                                <button
                                  onClick={() => removeFromCart(item.id)}
                                  aria-label={`Tirar ${item.name}`}
                                  className="flex h-9 w-9 items-center justify-center rounded-full bg-pagali-gray-100 text-pagali-gray-600 hover:bg-pagali-gray-200"
                                >
                                  <FiMinus />
                                </button>
                                <span className="w-5 text-center font-semibold">{quantity}</span>
                              </>
                            )}
                            <button
                              onClick={() => addToCart(item)}
                              aria-label={`Adicionar ${item.name}`}
                              className="flex h-9 w-9 items-center justify-center rounded-full bg-pagali-purple text-white hover:bg-pagali-purple-light"
                            >
                              <FiPlus />
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm text-pagali-error">Indisponível</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Detalhe do prato */}
      {selectedItem && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center" onClick={() => setSelectedItem(null)}>
          <div
            className="max-h-[85vh] w-full overflow-y-auto rounded-t-2xl bg-white sm:w-[28rem] sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-56 bg-pagali-gray-100">
              <ImagemPrato src={selectedItem.imageUrl} alt={selectedItem.name} className="h-full w-full object-cover" icone="text-5xl" />
              <button
                onClick={() => setSelectedItem(null)}
                aria-label="Fechar"
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-pagali-gray-600 hover:bg-white"
              >
                <FiX />
              </button>
            </div>
            <div className="p-5">
              <div className="mb-2 flex items-start justify-between">
                <h2 className="text-xl font-bold text-pagali-gray-800">{selectedItem.name}</h2>
                <span className="ml-3 whitespace-nowrap text-xl font-bold text-pagali-purple">{formatPrice(selectedItem.price)}</span>
              </div>
              {selectedItem.categoryName && (
                <span className="mb-3 inline-block rounded-full bg-pagali-purple/10 px-2 py-1 text-xs text-pagali-purple">
                  {selectedItem.categoryName}
                </span>
              )}
              {selectedItem.description && <p className="mb-4 text-sm text-pagali-gray-600">{selectedItem.description}</p>}
              {selectedItem.prepTime && (
                <div className="mb-4 flex items-center gap-2 text-sm text-pagali-gray-500">
                  <FiClock /> <span>Preparação: ~{selectedItem.prepTime} min</span>
                </div>
              )}
              {selectedItem.available !== false ? (
                <div className="flex items-center gap-3">
                  {getItemQuantity(selectedItem.id) > 0 && (
                    <div className="flex items-center gap-3 rounded-xl bg-pagali-gray-100 px-3 py-2">
                      <button
                        onClick={() => removeFromCart(selectedItem.id)}
                        aria-label="Tirar um"
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-pagali-gray-600 shadow-sm"
                      >
                        <FiMinus />
                      </button>
                      <span className="w-6 text-center text-lg font-semibold">{getItemQuantity(selectedItem.id)}</span>
                    </div>
                  )}
                  <button
                    onClick={() => addToCart(selectedItem)}
                    className="flex-1 rounded-xl bg-pagali-purple py-3 font-semibold text-white transition-colors hover:bg-pagali-purple-light"
                  >
                    {getItemQuantity(selectedItem.id) > 0
                      ? `Adicionar mais (${formatPrice(Number(selectedItem.price) * (getItemQuantity(selectedItem.id) + 1))})`
                      : `Adicionar ${formatPrice(selectedItem.price)}`}
                  </button>
                </div>
              ) : (
                <div className="rounded-xl bg-pagali-error-light py-3 text-center font-medium text-pagali-error">Produto indisponível</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp do restaurante — por cima da barra do carrinho quando ela aparece */}
      {restaurant.whatsapp && (
        <a
          href={`https://wa.me/${restaurant.whatsapp}?text=${encodeURIComponent(`Olá ${restaurant.name}! Vim pelo Pagali Food.`)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Falar com ${restaurant.name} no WhatsApp`}
          title={`Falar com ${restaurant.name} no WhatsApp`}
          className={`fixed right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-105 md:right-6 ${
            cartItemCount > 0 ? 'bottom-28 md:bottom-32' : 'bottom-6'
          }`}
        >
          <IoLogoWhatsapp className="h-8 w-8" />
        </a>
      )}

      {/* Carrinho: barra em baixo no telemóvel, botão flutuante à direita no computador */}
      {cartItemCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-pagali-gray-200 bg-white p-4 shadow-modal md:bottom-6 md:left-auto md:right-6 md:w-96 md:rounded-2xl md:border-0">
          <button
            onClick={() => navigate('/food/cart')}
            className="flex w-full items-center justify-between rounded-xl bg-pagali-purple px-6 py-4 font-semibold text-white transition-colors hover:bg-pagali-purple-light"
          >
            <span className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-sm">{cartItemCount}</span>
              Ver carrinho
            </span>
            <span>{formatPrice(Number(cartTotal) + Number(restaurant.deliveryFee || 0))}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default Restaurant;
